/// <reference types="temporal-polyfill/global" />

/**
 * Prisma ↔ Temporal bridge and UTC → local conversion utilities.
 *
 * Prisma returns native `Date` objects for all `DateTime` columns.
 * The **server-side** functions convert between `Date` and
 * `Temporal.Instant` at the persistence boundary so application code
 * can work with the Temporal API exclusively.
 *
 * The **client-side** functions convert UTC ISO-8601 strings (returned
 * by the API) into the user's local timezone for display. All three
 * micro-frontends receive timestamps as UTC strings — these helpers
 * ensure correct local rendering regardless of the device's timezone.
 *
 * **Polyfill-agnostic** — every function references `globalThis.Temporal`
 * (provided today by `temporal-polyfill/global`, loaded at each entry
 * point via `@learnai/tools/temporal-polyfill`). When runtimes ship
 * native Temporal, these utilities continue to work with zero changes.
 *
 * @module temporal
 */

// ---------------------------------------------------------------------------
// Server-side: Prisma ↔ Temporal conversion
// ---------------------------------------------------------------------------

/** Convert a Prisma `Date` to a `Temporal.Instant`. */
export function toInstant(date: Date): Temporal.Instant {
	return Temporal.Instant.fromEpochMilliseconds(date.getTime());
}

/** Convert a `Temporal.Instant` to a `Date` (for Prisma writes). */
export function toDate(instant: Temporal.Instant): Date {
	return new Date(instant.epochMilliseconds);
}

// ---------------------------------------------------------------------------
// Server-side: Serialization (Date → ISO-8601 UTC string for API responses)
// ---------------------------------------------------------------------------

/** Serialize a Prisma `Date` to an ISO-8601 UTC instant string. */
export function toISO(date: Date): string {
	return toInstant(date).toString();
}

/** Serialize an optional Prisma `Date` to ISO-8601 UTC or `null`. */
export function toISOOrNull(date: Date | null | undefined): string | null {
	return date ? toISO(date) : null;
}

// ---------------------------------------------------------------------------
// Server-side: Plain-date serialization (for `@db.Date` columns)
// ---------------------------------------------------------------------------

/**
 * Serialize a Prisma `Date` from a `@db.Date` column to an ISO-8601
 * calendar date string (`YYYY-MM-DD`).
 *
 * PostgreSQL `date` columns have no time component. Prisma deserializes
 * them as `Date` objects set to midnight UTC. This function converts
 * through `Temporal.Instant` → `ZonedDateTime` (UTC) → `PlainDate` to
 * produce a clean date-only string.
 */
export function toPlainDateISO(date: Date): string {
	return toInstant(date).toZonedDateTimeISO('UTC').toPlainDate().toString();
}

/**
 * Serialize an optional Prisma `@db.Date` to an ISO-8601 date string
 * (`YYYY-MM-DD`) or `null`.
 */
export function toPlainDateISOOrNull(date: Date | null | undefined): string | null {
	return date ? toPlainDateISO(date) : null;
}

/**
 * Parse an ISO-8601 calendar date string (`YYYY-MM-DD`) into a `Date`
 * suitable for Prisma `@db.Date` column writes.
 *
 * The resulting `Date` is set to midnight UTC — matching what Prisma
 * and PostgreSQL expect for date-only columns.
 */
export function fromPlainDateISO(iso: string): Date {
	const plain = Temporal.PlainDate.from(iso);
	return toDate(plain.toZonedDateTime('UTC').toInstant());
}

// ---------------------------------------------------------------------------
// Client-side: UTC → local timezone conversion
// ---------------------------------------------------------------------------

/**
 * Convert a UTC ISO-8601 string (from an API response) to a
 * `Temporal.ZonedDateTime` in the user's local timezone.
 *
 * Use this when you need the full local date and time — e.g.
 * displaying "January 15, 2024 at 3:30 PM" for a `createdAt`.
 *
 * The returned `ZonedDateTime` supports `.toLocaleString()` for
 * locale-aware formatting and exposes `.year`, `.month`, `.day`,
 * `.hour`, `.minute`, `.second` for manual rendering.
 *
 * @param iso - A UTC ISO-8601 timestamp from the API (e.g. `"2024-01-15T10:30:00Z"`).
 * @param timeZone - IANA timezone identifier. Defaults to the device's local timezone.
 */
export function toLocalDateTime(iso: string, timeZone?: string): Temporal.ZonedDateTime {
	const tz = timeZone ?? Temporal.Now.timeZoneId();
	return Temporal.Instant.from(iso).toZonedDateTimeISO(tz);
}

/**
 * Convert a UTC ISO-8601 string to a `Temporal.PlainDate` in the
 * user's local timezone.
 *
 * Use this when you only need the calendar date — e.g. displaying
 * "Due: Jan 15, 2024" for a `dueAt` field.
 *
 * **Timezone matters here**: a UTC timestamp of `"2024-01-15T22:00:00Z"`
 * is January 15 in New York (UTC−5) but January 16 in Tokyo (UTC+9).
 *
 * @param iso - A UTC ISO-8601 timestamp from the API.
 * @param timeZone - IANA timezone identifier. Defaults to the device's local timezone.
 */
export function toLocalDate(iso: string, timeZone?: string): Temporal.PlainDate {
	return toLocalDateTime(iso, timeZone).toPlainDate();
}

/**
 * Convert a UTC ISO-8601 string to a `Temporal.PlainTime` in the
 * user's local timezone.
 *
 * Use this when you only need the wall-clock time — e.g. displaying
 * "3:30 PM" for a chat message timestamp.
 *
 * @param iso - A UTC ISO-8601 timestamp from the API.
 * @param timeZone - IANA timezone identifier. Defaults to the device's local timezone.
 */
export function toLocalTime(iso: string, timeZone?: string): Temporal.PlainTime {
	return toLocalDateTime(iso, timeZone).toPlainTime();
}

// ---------------------------------------------------------------------------
// Client-side: Long (natural language) date/time formatting
// ---------------------------------------------------------------------------

function ordinalSuffix(n: number): string {
	const mod10 = n % 10;
	const mod100 = n % 100;
	if (mod10 === 1 && mod100 !== 11) return 'st';
	if (mod10 === 2 && mod100 !== 12) return 'nd';
	if (mod10 === 3 && mod100 !== 13) return 'rd';
	return 'th';
}

/**
 * Format a UTC ISO-8601 timestamp for display in natural language in the user's local timezone.
 *
 * Example: "February 26th, 2026, 5:00 PM"
 *
 * @param iso - A UTC ISO-8601 timestamp from the API.
 * @param timeZone - IANA timezone identifier. Defaults to the device's local timezone.
 */
export function formatDateTimeLong(iso: string, timeZone?: string): string {
	const zdt = toLocalDateTime(iso, timeZone);
	const month = zdt.toLocaleString(undefined, { month: 'long' });
	const day = zdt.day;
	const year = zdt.year;
	const time = zdt.toLocaleString(undefined, { timeStyle: 'short' });
	return `${month} ${day}${ordinalSuffix(day)}, ${year}, ${time}`;
}

/**
 * Format an ISO-8601 calendar date (YYYY-MM-DD) for display in natural language.
 *
 * Example: "February 26th, 2026"
 *
 * @param iso - A date-only string from the API (e.g. birthday).
 */
export function formatDateLong(iso: string): string {
	const d = Temporal.PlainDate.from(iso);
	// Use a Date at local midnight for locale-aware month name (PlainDate may not have toLocaleString in polyfill).
	const month = new Date(d.year, d.month - 1, 1).toLocaleString(undefined, { month: 'long' });
	return `${month} ${d.day}${ordinalSuffix(d.day)}, ${d.year}`;
}
